import { ApolloError } from '@apollo/client/errors';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { celticsRights, eeRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import {
  applyUserAction,
  discardDocument,
  getDownloadDocumentURL,
  removeUpload,
} from 'api/operations/documentsService';
import { sendSignFileReminder } from 'api/operations/signService';
import { retrieveNetworkErrorFromApollo } from 'global/utils/retrieveNetworkErrorFromApollo';
import {
  DocumentAccessRole,
  ActionObject,
  DocumentAction,
  DocumentDomainProperties,
  DocumentStatus,
  UploadStep,
  UserAction,
} from 'types/documentsService';

const makeApiCall = async (
  apiHandler: () => Promise<void>,
  refreshData?: () => Promise<void>,
  setIsLoading?: (status: boolean) => void,
): Promise<void> => {
  if (setIsLoading) {
    setIsLoading(true);
  }

  await apiHandler();

  if (refreshData) {
    await refreshData();
  }
  if (setIsLoading) {
    setIsLoading(false);
  }
};

export const allActions: ActionObject[] = [
  {
    type: DocumentAction.DOWNLOAD,
    name: 'Download Document',
    showForRoles: null,
    enableForRoles: null,
    showForStatuses: [
      DocumentStatus.UPLOADED,
      DocumentStatus.SIGNATURE_PENDING,
      DocumentStatus.SIGNATURE_REQUEST_FAILED,
      DocumentStatus.SIGNED,
      DocumentStatus.APPROVED,
      DocumentStatus.REJECTED,
    ],
    hideOnPages: null,
    handler: async (context): Promise<void> => {
      await makeApiCall(
        async (): Promise<void> => {
          const downloadUrl = await getDownloadDocumentURL(context.static.documentId);
          downloadByUrl(downloadUrl);
        },
        undefined,
        context.interactions.setCardIsLoading,
      );
    },
  },
  {
    type: DocumentAction.APPROVE,
    name: 'Approve Document',
    showForRoles: null,
    enableForRoles: [DocumentAccessRole.SPONSOR],
    showForStatuses: [DocumentStatus.UPLOADED, DocumentStatus.SIGNED, DocumentStatus.REJECTED],
    hideOnPages: null,
    handler: async (context): Promise<void> => {
      await makeApiCall(
        async (): Promise<void> => {
          await applyUserAction(context.static.documentId, UserAction.APPROVE);
        },
        context.interactions.refreshData,
        context.interactions.setPageIsLoading,
      );
    },
  },
  {
    type: DocumentAction.REJECT,
    name: 'Reject Document',
    showForRoles: null,
    enableForRoles: [DocumentAccessRole.SPONSOR],
    showForStatuses: [DocumentStatus.UPLOADED, DocumentStatus.SIGNED, DocumentStatus.APPROVED],
    hideOnPages: null,
    handler: async (context): Promise<void> => {
      await makeApiCall(
        async (): Promise<void> => {
          await applyUserAction(context.static.documentId, UserAction.REJECT);
        },
        context.interactions.refreshData,
        context.interactions.setPageIsLoading,
      );
    },
  },
  {
    type: DocumentAction.BYPASS,
    name: 'Bypass Document',
    showForRoles: [DocumentAccessRole.SPONSOR, DocumentAccessRole.FREELOADER],
    enableForRoles: null,
    showForStatuses: [DocumentStatus.NOT_UPLOADED],
    hideOnPages: null,
    handler: async (context): Promise<void> => {
      await makeApiCall(
        async (): Promise<void> => {
          await applyUserAction(context.static.documentId, UserAction.BYPASS);
        },
        context.interactions.refreshData,
        context.interactions.setPageIsLoading,
      );
    },
  },
  {
    type: DocumentAction.EDIT,
    name: 'Edit Document',
    showForRoles: [DocumentAccessRole.SPONSOR, DocumentAccessRole.FREELOADER],
    enableForRoles: null,
    showForStatuses: [DocumentStatus.NOT_UPLOADED],
    hideOnPages: null,
    handler: (context): void => {
      if (!context.ui.openEditModal) {
        throw new Error('No openEditModal callback provided for EDIT action!');
      }
      context.ui.openEditModal();
    },
  },
  {
    type: DocumentAction.EDIT_TEMPLATE,
    name: 'Edit Template',
    showForRoles: [DocumentAccessRole.SPONSOR, DocumentAccessRole.FREELOADER],
    enableForRoles: null,
    showForStatuses: [DocumentStatus.NOT_UPLOADED],
    hideOnPages: null,
    handler: (context): void => {
      if (!context.ui.openEditTemplateModal) {
        throw new Error('No openEditTemplateModal callback provided for EDIT_TEMPLATE action!');
      }
      context.ui.openEditTemplateModal();
    },
  },
  {
    type: DocumentAction.REMOVE_UPLOAD,
    name: 'Remove Upload',
    showForRoles: null,
    enableForRoles: null,
    showForStatuses: [
      DocumentStatus.UPLOADED,
      DocumentStatus.REJECTED,
      DocumentStatus.SIGNATURE_REQUEST_FAILED,
      DocumentStatus.SIGNATURE_PENDING,
    ],
    hideOnPages: null,
    handler: async (context): Promise<void> => {
      await makeApiCall(
        async (): Promise<void> => {
          await removeUpload(context.static.documentId);
        },
        context.interactions.refreshData,
        context.interactions.setPageIsLoading,
      );
    },
  },
  {
    type: DocumentAction.DISCARD,
    name: 'Discard Document',
    showForRoles: [DocumentAccessRole.SPONSOR, DocumentAccessRole.FREELOADER],
    enableForRoles: null,
    showForStatuses: [
      DocumentStatus.NOT_UPLOADED,
      DocumentStatus.UPLOADED,
      DocumentStatus.REJECTED,
      DocumentStatus.SIGNATURE_REQUEST_FAILED,
      DocumentStatus.SIGNATURE_PENDING,
    ],
    hideOnPages: null,
    handler: async (context): Promise<void> => {
      await makeApiCall(
        async (): Promise<void> => {
          await discardDocument(context.static.documentId);
        },
        context.interactions.refreshData,
        context.interactions.setPageIsLoading,
      );
    },
  },
  {
    type: DocumentAction.REQUEST_FOR_SIGNATURE,
    name: 'Request for Signature',
    showForRoles: [DocumentAccessRole.SPONSOR, DocumentAccessRole.FREELOADER],
    enableForRoles: null,
    showForStatuses: [DocumentStatus.UPLOADED],
    hideOnPages: [UploadStep.INITIATE_RFQ, UploadStep.RFQ_PROPOSAL],
    handler: (context): void => {
      if (!context.ui.openRequestSignModal) {
        throw new Error('No openRequestSignModal callback provided for REQUEST_FOR_SIGNATURE action!');
      }
      context.ui.openRequestSignModal();
    },
  },

  {
    type: DocumentAction.SEND_SIGN_REMINDER,
    name: 'Send Sign Reminder',
    showForRoles: null,
    enableForRoles: null,
    showForStatuses: [DocumentStatus.SIGNATURE_PENDING],
    hideOnPages: [UploadStep.INITIATE_RFQ, UploadStep.RFQ_PROPOSAL],
    handler: async (context): Promise<void> => {
      try {
        await makeApiCall(
          async (): Promise<void> => {
            await sendSignFileReminder(context.static.documentId);
          },
          undefined,
          context.interactions.setCardIsLoading,
        );
      } catch (error) {
        context.ui.showBeError(retrieveNetworkErrorFromApollo(error as ApolloError));
        context.interactions.setCardIsLoading(false);
        return;
      }

      if (!context.ui.showSentNotification) {
        throw new Error('No showSentNotification callback provided for SEND_SIGN_REMINDER action!');
      }
      context.ui.showSentNotification();
    },
  },
  {
    type: DocumentAction.CHANGE_SIGNATURE_EMAIL,
    name: 'Change Signature Email',
    showForRoles: [DocumentAccessRole.SPONSOR, DocumentAccessRole.FREELOADER],
    enableForRoles: null,
    showForStatuses: [DocumentStatus.SIGNATURE_PENDING, DocumentStatus.SIGNATURE_REQUEST_FAILED],
    hideOnPages: [UploadStep.INITIATE_RFQ, UploadStep.RFQ_PROPOSAL],
    handler: (context): void => {
      if (!context.ui.openRequestSignModal) {
        throw new Error('No openRequestSignModal callback provided for CHANGE_SIGNATURE_EMAIL action!');
      }
      context.ui.openRequestSignModal();
    },
  },
];

/** 0 index is the topmost */
const ACTION_PRIORITIES: Record<DocumentAction, number> = Object.freeze({
  [DocumentAction.DOWNLOAD]: 0,
  [DocumentAction.BYPASS]: 1,
  [DocumentAction.EDIT]: 1,
  [DocumentAction.EDIT_TEMPLATE]: 1,
  [DocumentAction.REQUEST_FOR_SIGNATURE]: 1,
  [DocumentAction.SEND_SIGN_REMINDER]: 2,
  [DocumentAction.CHANGE_SIGNATURE_EMAIL]: 2,
  [DocumentAction.APPROVE]: 2,
  [DocumentAction.REJECT]: 3,
  [DocumentAction.REMOVE_UPLOAD]: 4,
  [DocumentAction.DISCARD]: 4,
});

export function determineDocumentAccessRole(userRoles: SupportedUserRoles[]): DocumentAccessRole {
  const isSponsor = celticsRights(userRoles);
  if (isSponsor) {
    return DocumentAccessRole.SPONSOR;
  }

  const isErOrEe = eeRights(userRoles);
  if (isErOrEe) {
    return DocumentAccessRole.ER_EE;
  }

  return DocumentAccessRole.FREELOADER;
}

const pageTypeFilter =
  (pageType: UploadStep) =>
  (actions: ActionObject[]): ActionObject[] => {
    return actions.filter((action) => !action.hideOnPages || !action.hideOnPages.includes(pageType));
  };

const domainPropertyFilter =
  (domainProperties: DocumentDomainProperties) =>
  (actions: ActionObject[]): ActionObject[] => {
    return actions.filter((action) => !domainProperties.excludeActions.includes(action.type));
  };

const documentStatusFilter =
  (documentStatus: DocumentStatus) =>
  (actions: ActionObject[]): ActionObject[] => {
    return actions.filter((action) => !action.showForStatuses || action.showForStatuses.includes(documentStatus));
  };

const showForRoleFilter =
  (role: DocumentAccessRole) =>
  (actions: ActionObject[]): ActionObject[] => {
    return actions.filter((action) => !action.showForRoles || action.showForRoles.includes(role));
  };

const enableForRoleFilter =
  (role: DocumentAccessRole) =>
  (actions: ActionObject[]): ActionObject[] => {
    return actions.map((action) => ({
      ...action,
      isDisabled: !!action.enableForRoles && !action.enableForRoles.includes(role),
    }));
  };

const sortingFilter = (actions: ActionObject[]): ActionObject[] => {
  return actions.sort((a, b) => {
    const priorityA = ACTION_PRIORITIES[a.type] ?? Infinity;
    const priorityB = ACTION_PRIORITIES[b.type] ?? Infinity;
    return priorityA - priorityB;
  });
};

const composeFilters = (...filters: ((actions: ActionObject[]) => ActionObject[])[]) => {
  return (actions: ActionObject[]): ActionObject[] =>
    filters.reduce((currentActions, filter) => filter(currentActions), actions);
};

export interface PipelineSettings {
  userRoles: SupportedUserRoles[];
  pageType: UploadStep;
  documentStatus: DocumentStatus;
  domainProperties: DocumentDomainProperties;
}

export const actionsFiltersPipeline = (settings: PipelineSettings): ActionObject[] =>
  composeFilters(
    enableForRoleFilter(determineDocumentAccessRole(settings.userRoles)),
    showForRoleFilter(determineDocumentAccessRole(settings.userRoles)),
    pageTypeFilter(settings.pageType),
    documentStatusFilter(settings.documentStatus),
    domainPropertyFilter(settings.domainProperties),
    sortingFilter,
  )(allActions);
